import { Brand } from '@stax-core/types-omni';
import { shadeHexColor } from '../../util/color.util';

export const convertBrandToTheme = (brand: Partial<Brand>) => {
  return {
    name: 'self-reg',
    brandName: brand.name,
    colors: {
      primaryColor: '#' + brand.primaryColor,
      sidebarText: '#' + brand.sidebarText,
      tertiaryColor: '#' + brand.tertiaryColor,
      accentText: '#' + brand.accentText,
      headerAccentText: '#' + brand.headerAccentText,
      sidebarActiveTab: '#' + brand.sidebarActiveTab,
      hyperlink: '#' + brand.hyperlink,
      primaryAccent: '#' + brand.primaryAccent,
      secondaryColor: '#' + brand.secondaryColor,
      secondaryAccent: '#' + brand.secondaryAccent,
      sidebarBackground: '#' + brand.sidebarBackground,
      disabledColor: '#BDC9CC',
    },
    fonts: {
      body: 'inherit',
      heading: 'inherit',
      monospace: 'inherit',
    },
    fontSizes: [12, 14, 16, 20, 24, 32, 48, 64, 96],
    fontWeights: {
      body: 400,
      heading: 700,
      bold: 700,
    },
    lineHeights: {
      body: 1.5,
      heading: 1.25,
    },
    space: [0, 4, 8, 16, 32, 64, 128, 256, 512],
    sizes: {
      avatar: 48,
    },
    radii: {
      default: 4,
      circle: 99999,
    },
    shadows: {
      card: '0 0 4px rgba(0, 0, 0, .125)',
    },
    text: {
      heading: {
        fontFamily: 'heading',
        lineHeight: 'heading',
        fontWeight: 'heading',
      },
      display: {
        fontFamily: 'body',
        fontWeight: 'body',
        lineHeight: 'body',
        fontSize: [5, 6, 7],
      },
      caps: {
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
      },
    },
    variants: {
      avatar: {
        width: 'avatar',
        height: 'avatar',
        borderRadius: 'circle',
      },
      card: {
        p: 2,
        bg: 'background',
        boxShadow: 'card',
      },
      link: {
        color: 'primary',
      },
      nav: {
        fontSize: 1,
        fontWeight: 'bold',
        display: 'inline-block',
        p: 2,
        color: 'inherit',
        textDecoration: 'none',
        ':hover,:focus,.active': {
          color: 'primary',
        },
      },
    },
    buttons: {
      primary: {
        fontSize: 2,
        fontWeight: 'bold',
        color: 'background',
        bg: '#' + brand.primaryAccent,
        borderRadius: 'default',
      },
      outline: {
        variant: 'buttons.primary',
        color: 'primary',
        bg: 'transparent',
        boxShadow: 'inset 0 0 2px',
      },
      secondary: {
        variant: 'buttons.primary',
        color: 'background',
        bg: 'secondary',
      },
    },
    styles: {
      root: {
        fontFamily: 'body',
        fontWeight: 'body',
        lineHeight: 'body',
      },
    },
    component: {
      button: {
        primary: {
          large: {
            states: {
              default: {
                backgroundColor: '#062333',
                borderColor: '#062333',
                color: '#ffffff',
              },
              hover: {
                backgroundColor:
                  brand.staxPrimary &&
                  shadeHexColor(`#${brand.staxPrimary}`, -0.2),
                borderColor:
                  brand.staxPrimary &&
                  shadeHexColor(`#${brand.staxPrimary}`, -0.2),
              },
              active: {
                backgroundColor:
                  brand.staxPrimary &&
                  shadeHexColor(`#${brand.staxPrimary}`, -0.1),
                borderColor:
                  brand.staxPrimary &&
                  shadeHexColor(`#${brand.staxPrimary}`, -0.1),
              },
              disabled: {
                backgroundColor: '#BDC9CC',
                borderColor: '#BDC9CC',
              },
              focus: {
                backgroundColor: '#BDC9CC',
                borderColor: '#BDC9CC',
              },
            },
          },
        },
        secondary: {
          large: {
            states: {
              hover: {
                gradientColor:
                  brand.staxPrimary &&
                  shadeHexColor(`#${brand.staxPrimary}`, 0.8),
              },
              activate: {
                gradientColor:
                  brand.staxPrimary &&
                  shadeHexColor(`#${brand.staxPrimary}`, 0.1),
              },
            },
          },
        },
      },
      checkbox: {
        backgroundColor: 'transparent',
        borderColor: 'lightgray',
        borderRadius: '5px',
        color: 'white',
        checked: {
          /* Use secondary brand color for checked background */
          backgroundColor: '#' + brand.secondaryColor,
        },
        disabled: {
          backgroundColor: '#BDC9CC',
          borderColor: '#BDC9CC',
          color: '#BDC9CC',
        },
        error: {
          backgroundColor: '#f07587',
          borderColor: '#f07587',
        },
      },
      textField: {
        /* Added so truffle Checkbox with label no longer crashes when reading labelColor */
        labelColor: '#' + (brand.primaryColor || '062333'),
        /* Added full structure to satisfy truffle TextField expectations */
        subElementSpacing: { x: 8, y: 8 },
        input: {
          backgroundColor: '#ffffff',
          borderColor: '#BDC9CC',
          focus: {
            borderColor:
              (brand.staxPrimary &&
                shadeHexColor(`#${brand.staxPrimary}`, -0.2)) ||
              '#062333',
          },
          disabled: {
            backgroundColor: '#F5F7F8',
            borderColor: '#E1E6E8',
            color: '#8D9799',
          },
        },
        helperText: {
          color: '#8D9799',
          errorColor: '#E30029',
        },
      },
      label: {
        fontSize: 14,
        color: '#' + (brand.primaryColor || '062333'),
        states: {
          default: {
            color: '#' + (brand.primaryColor || '062333'),
          },
          disabled: {
            color: '#8D9799',
          },
          error: {
            color: '#E30029',
          },
        },
      },
      tabs: {
        borderColor: '#DDDFE4',
        selectedColor: '#' + (brand.secondaryColor || 'B93BE4'),
        color: '#627684',
      },
    },
  };
};

export const defaultTheme = convertBrandToTheme({
  primaryColor: '3d4d65',
  secondaryColor: '0099cc',
  tertiaryColor: '0099cc',
  primaryAccent: '3fa2f7',
  accentText: 'FFFFFF',
  hyperlink: '3fa2f7',
  sidebarBackground: '3d4d65',
  sidebarText: 'fafafa',
  sidebarActiveTab: '09c',
  headerAccent: '22bb60',
  headerAccentText: 'fafafa',
});
